import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import {
  Room,
  RoomOptions,
  RemoteTrack,
  RoomEvent,
  Track,
  Participant,
  ConnectionState,
  TrackPublication,
  LocalAudioTrack,
} from 'livekit-client';
import ky from '@/utils/ky';

export interface AvatarConfig {
  firstMessage: string;
  voiceId?: string;
  roomName: string;
  participantName: string;
  avatarImage?: string;
  avatarId?: string;
  avatarProvider?: "hedra" | "tavus";
  tavusReplicaId?: string;
  tavusPersonaId?: string;
}

export interface AvatarCallbacks {
  onConnect?: () => void;
  onError?: (error: any) => void;
  onMessage?: (data: { source: 'ai' | 'user'; message: string }) => void;
  onVideoReady?: (videoElement: HTMLVideoElement) => void;
  onAgentStateChange?: (state: string) => void;
}

interface UseLivekitAvatarParams {
  roomName?: string;
  participantName?: string;
  metadata?: any;
  onConnectionChange?: (isConnected: boolean) => void;
  onAgentSpeaking?: (isSpeaking: boolean) => void;
}

interface UseLivekitAvatarReturn {
  // Basic LiveKit functionality
  room: Room | null;
  isConnected: boolean;
  isConnecting: boolean;
  connectionState: ConnectionState;
  
  // Video-specific functionality
  agentVideoTrack: TrackPublication | null;
  videoElementRef: React.RefObject<HTMLVideoElement>;
  isVideoReady: boolean;
  
  // Audio/Microphone functionality
  isMicrophoneActive: boolean;
  startMicrophone: () => Promise<void>;
  stopMicrophone: () => void;
  
  // Agent state
  isAgentSpeaking: boolean;
  agentState: string | null;
  
  // Control methods
  connectAvatar: (
    roomName?: string,
    participantName?: string,
    metadata?: any,
  ) => Promise<void>;
  disconnectAvatar: () => void;
  
  // Avatar-specific methods
  startAvatarSession: (
    config: AvatarConfig,
    callbacks?: AvatarCallbacks,
  ) => Promise<string>;
  stopAvatarSession: () => Promise<void>;
  
  // Video container management
  attachToVideoContainer: (containerRef: React.RefObject<HTMLDivElement>) => void;
  
  error: string | null;
}

export const useLivekitAvatar = (
  params: UseLivekitAvatarParams = {},
): UseLivekitAvatarReturn => {
  // Reduce noise - only log once per mount, not on every param change
  const mountedRef = useRef(false);
  if (!mountedRef.current) {
    console.log('🎭 DEBUG: useLivekitAvatar hook mounted');
    mountedRef.current = true;
  }
  
  const {
    roomName: defaultRoomName,
    participantName: defaultParticipantName,
    metadata: defaultMetadata,
    onConnectionChange,
    onAgentSpeaking,
  } = params;

  // Basic room state
  const [room, setRoom] = useState<Room | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.Disconnected,
  );
  const [error, setError] = useState<string | null>(null);

  // Video-specific state
  const [agentVideoTrack, setAgentVideoTrack] = useState<TrackPublication | null>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [agentState, setAgentState] = useState<string | null>(null);

  // Audio/Microphone state
  const [isMicrophoneActive, setIsMicrophoneActive] = useState(false);

  // Refs
  const videoElementRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const cleanupFunctionsRef = useRef<(() => void)[]>([]);
  const callbacksRef = useRef<AvatarCallbacks>({});
  const audioTrackRef = useRef<LocalAudioTrack | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  const roomOptions = useMemo((): RoomOptions => ({
    adaptiveStream: true,
    dynacast: true,
    publishDefaults: {
      simulcast: true,
    },
  }), []);



  // Room event listeners setup for avatar functionality - optimize dependencies
  const setupRoomListeners = useCallback(
    (room: Room) => {
      const handleTrackSubscribed = (
        track: RemoteTrack,
        publication: TrackPublication,
        participant: Participant,
      ) => {
        console.log('🎭 DEBUG: Track subscribed:', {
          trackKind: track.kind,
          participantIdentity: participant.identity,
          trackSid: track.sid,
          publicationSource: publication?.source,
          isAgent: participant.identity.startsWith('agent-'),
          timestamp: new Date().toISOString(),
        });

        // Handle tracks from agent participants (including avatar agents like tavus-avatar-agent)
        if (participant.identity.startsWith('agent-') || participant.identity.includes('avatar')) {
          if (track.kind === Track.Kind.Video) {
            console.log('🎭 DEBUG: Agent video track detected, storing publication');
            setAgentVideoTrack(publication);
            
            // Attach to external video container if available
            if (videoContainerRef.current) {
              try {
                // Clear any existing video elements in the container
                while (videoContainerRef.current.firstChild) {
                  videoContainerRef.current.removeChild(videoContainerRef.current.firstChild);
                }

                // Create and attach video element to the external container
                const videoElement = document.createElement('video');
                videoElement.autoplay = true;
                videoElement.playsInline = true;
                videoElement.muted = true; // LiveKit handles audio separately
                videoElement.style.width = '100%';
                videoElement.style.height = '100%';
                videoElement.style.objectFit = 'contain';
                videoElement.style.backgroundColor = '#000';

                // Attach the track to the new video element
                track.attach(videoElement);
                
                // Add to the container
                videoContainerRef.current.appendChild(videoElement);
                videoElementRef.current = videoElement;
                
                setIsVideoReady(true);
                callbacksRef.current.onVideoReady?.(videoElement);
                
                console.log('🎭 DEBUG: Video track attached to external container successfully');
              } catch (error) {
                console.error('Failed to attach video track to container:', error);
                setError('Failed to display video');
              }
            }
          } else if (track.kind === Track.Kind.Audio) {
            console.log('🎭 DEBUG: Agent audio track detected, creating audio element for playback');
            
            try {
              // Create an audio element to play the agent's voice
              const audioElement = document.createElement('audio');
              audioElement.autoplay = true;
              audioElement.muted = false; // We want to hear the agent's voice
              
              // Attach the audio track to the audio element
              track.attach(audioElement);
              
              // Add the audio element to the document body (invisible)
              document.body.appendChild(audioElement);
              
              console.log('🔊 DEBUG: Agent audio track attached and playing:', {
                trackSid: track.sid,
                audioElementId: audioElement.id || 'unnamed',
                autoplay: audioElement.autoplay,
                muted: audioElement.muted,
              });
              
              // Store reference for cleanup
              audioElement.setAttribute('data-livekit-agent-audio', 'true');
              
            } catch (error) {
              console.error('Failed to attach agent audio track:', error);
              setError('Failed to play agent audio');
            }
          }
        }
      };

      const handleTrackUnsubscribed = (
        track: RemoteTrack,
        _publication: TrackPublication,
        participant: Participant,
      ) => {
        console.log('🎭 DEBUG: Track unsubscribed:', {
          trackKind: track.kind,
          participantIdentity: participant.identity,
          trackSid: track.sid,
          timestamp: new Date().toISOString(),
        });

        if (participant.identity.startsWith('agent-') || participant.identity.includes('avatar')) {
          if (track.kind === Track.Kind.Video) {
            console.log('🎭 DEBUG: Agent video track unsubscribed, detaching');
            
            // Inline detach logic to avoid callback dependency
            if (videoContainerRef.current && videoElementRef.current) {
              // Clear the container
              while (videoContainerRef.current.firstChild) {
                videoContainerRef.current.removeChild(videoContainerRef.current.firstChild);
              }
              videoElementRef.current = null;
              setIsVideoReady(false);
            }
            setAgentVideoTrack(null);
          } else if (track.kind === Track.Kind.Audio) {
            console.log('🔊 DEBUG: Agent audio track unsubscribed, removing audio element');
            
            // Find and remove the agent audio element
            const agentAudioElements = document.querySelectorAll('audio[data-livekit-agent-audio="true"]');
            agentAudioElements.forEach((audioElement) => {
              if (audioElement.parentNode) {
                audioElement.parentNode.removeChild(audioElement);
                console.log('🔊 DEBUG: Removed agent audio element from DOM');
              }
            });
          }
        }
      };

      const handleParticipantAttributesChanged = (
        changedAttributes: Record<string, string>,
        participant: Participant,
      ) => {
        console.log('🎭 DEBUG: Participant attributes changed:', {
          participantIdentity: participant.identity,
          changedAttributes,
          timestamp: new Date().toISOString(),
        });

        if (participant.identity.startsWith('agent-')) {
          const agentState = changedAttributes['lk.agent.state'];
          
          if (agentState) {
            console.log('🎭 DEBUG: Agent state change:', {
              participantIdentity: participant.identity,
              newState: agentState,
              timestamp: new Date().toISOString(),
            });

            setAgentState(agentState);
            setIsAgentSpeaking(agentState === 'speaking');
            callbacksRef.current.onAgentStateChange?.(agentState);
          }
        }
      };

      const handleConnectionStateChanged = (state: ConnectionState) => {
        console.log('🎭 DEBUG: Connection state changed:', state);
        setConnectionState(state);
        setIsConnected(state === ConnectionState.Connected);
      };

      const handleDisconnected = () => {
        console.log('🎭 DEBUG: Room disconnected');
        setIsConnected(false);
        setIsConnecting(false);
        
        // Inline detach logic
        if (videoContainerRef.current && videoElementRef.current) {
          // Clear the container
          while (videoContainerRef.current.firstChild) {
            videoContainerRef.current.removeChild(videoContainerRef.current.firstChild);
          }
          videoElementRef.current = null;
          setIsVideoReady(false);
        }
        setAgentVideoTrack(null);
        
        // Clean up agent audio elements
        const agentAudioElements = document.querySelectorAll('audio[data-livekit-agent-audio="true"]');
        agentAudioElements.forEach((audioElement) => {
          if (audioElement.parentNode) {
            audioElement.parentNode.removeChild(audioElement);
          }
        });
      };

      // Set up event listeners
      room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
      room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
      room.on(RoomEvent.ParticipantAttributesChanged, handleParticipantAttributesChanged);
      room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
      room.on(RoomEvent.Disconnected, handleDisconnected);

      // Store cleanup functions
      const cleanup = () => {
        room.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
        room.off(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
        room.off(RoomEvent.ParticipantAttributesChanged, handleParticipantAttributesChanged);
        room.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
        room.off(RoomEvent.Disconnected, handleDisconnected);
      };

      cleanupFunctionsRef.current.push(cleanup);
    },
    [], // Remove all dependencies to prevent re-creation
  );

  // Core room connection for avatar - optimize dependencies
  const connectToRoom = useCallback(
    async (roomName: string, participantName: string, _metadata?: any) => {
      if (isConnecting || isConnected) {
        console.log('🎭 DEBUG: Already connecting or connected to room');
        return;
      }

      setIsConnecting(true);
      setError(null);

      try {
        // Inline getAccessToken to avoid dependency
        const response = await ky
          .get('livekit/token', {
            searchParams: {
              username: participantName,
              room: roomName,
            },
          })
          .json<{ token: string }>();

        const token = response.token;
        const livekitUrl = import.meta.env.VITE_LIVEKIT_URL;

        if (!livekitUrl) {
          throw new Error('VITE_LIVEKIT_URL environment variable is not set');
        }

        const newRoom = new Room(roomOptions);
        setupRoomListeners(newRoom);

        await newRoom.connect(livekitUrl, token);

        setRoom(newRoom);
        setIsConnecting(false);

        console.log('🎭 DEBUG: Successfully connected to LiveKit room:', roomName);
      } catch (error) {
        console.error('🎭 ERROR: Failed to connect to LiveKit room:', error);
        setIsConnecting(false);
        setError(error instanceof Error ? error.message : 'Connection failed');
        throw error;
      }
    },
    [isConnecting, isConnected, setupRoomListeners, roomOptions],
  );

  const connectAvatar = useCallback(
    async (roomName?: string, participantName?: string, metadata?: any) => {
      // Prevent duplicate connections
      if (isConnecting || isConnected) {
        console.log('🎭 DEBUG: connectAvatar called but already connecting/connected, skipping');
        return;
      }

      console.log('🎭 DEBUG: connectAvatar called:', {
        roomName,
        participantName,
        metadata,
      });
      
      try {
        setError(null);

        const finalRoomName = roomName || defaultRoomName;
        const finalParticipantName = participantName || defaultParticipantName;
        const finalMetadata = metadata || defaultMetadata;

        if (!finalRoomName || !finalParticipantName) {
          throw new Error('Room name and participant name are required');
        }

        await connectToRoom(finalRoomName, finalParticipantName, finalMetadata);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to connect avatar session';
        setError(errorMessage);
        throw err;
      }
    },
    [defaultRoomName, defaultParticipantName, defaultMetadata, connectToRoom, isConnecting, isConnected],
  );

  const disconnectFromRoom = useCallback(() => {
    if (room) {
      // Clean up event listeners
      cleanupFunctionsRef.current.forEach((cleanup) => cleanup());
      cleanupFunctionsRef.current = [];

      // Stop microphone before disconnecting
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
        audioTrackRef.current = null;
      }
      setIsMicrophoneActive(false);

      // Detach video track from external container
      if (videoContainerRef.current && videoElementRef.current) {
        // Clear the container
        while (videoContainerRef.current.firstChild) {
          videoContainerRef.current.removeChild(videoContainerRef.current.firstChild);
        }
        videoElementRef.current = null;
        setIsVideoReady(false);
      }
      setAgentVideoTrack(null);
      
      // Clean up agent audio elements
      const agentAudioElements = document.querySelectorAll('audio[data-livekit-agent-audio="true"]');
      agentAudioElements.forEach((audioElement) => {
        if (audioElement.parentNode) {
          audioElement.parentNode.removeChild(audioElement);
        }
      });

      // Disconnect from room
      room.disconnect();
      setRoom(null);
      setIsConnected(false);
      setIsConnecting(false);
      setIsAgentSpeaking(false);
      setAgentState(null);
      setConnectionState(ConnectionState.Disconnected);

      console.log('🎭 DEBUG: Disconnected from LiveKit room');
    }
  }, [room]);

  const disconnectAvatar = useCallback(() => {
    try {
      setError(null);
      disconnectFromRoom();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to disconnect avatar session';
      setError(errorMessage);
      console.error('🎭 ERROR: Error disconnecting avatar:', err);
    }
  }, [disconnectFromRoom]);

  const startAvatarSession = useCallback(
    async (
      config: AvatarConfig,
      callbacks: AvatarCallbacks = {},
    ): Promise<string> => {
      console.log('🎭 DEBUG: startAvatarSession called:', {
        roomName: config.roomName,
        participantName: config.participantName,
        currentlyConnected: isConnected,
        isConnecting,
      });

      // Prevent duplicate connections
      if (isConnecting || isConnected) {
        console.log('🎭 DEBUG: Avatar session already starting/started, skipping duplicate call');
        return `${config.roomName}-${config.participantName}`;
      }

      try {
        // Set connecting state first to prevent concurrent calls
        setIsConnecting(true);
        callbacksRef.current = callbacks;

        const metadata = {
          firstMessage: config.firstMessage,
          voiceId: config.voiceId,
          // language, prompt, and avatarProvider are handled server-side
        };

        console.log('🎭 DEBUG: Avatar session config:', metadata);

        // Connect to room and wait for full connection - inline token request
        const response = await ky
          .get('livekit/token', {
            searchParams: {
              username: config.participantName,
              room: config.roomName,
              ...(config.avatarImage && { avatarImage: config.avatarImage }),
              ...(config.avatarId && { avatarId: config.avatarId }),
              ...(config.avatarProvider && { avatarProvider: config.avatarProvider }),
              ...(config.tavusReplicaId && { tavusReplicaId: config.tavusReplicaId }),
              ...(config.tavusPersonaId && { tavusPersonaId: config.tavusPersonaId }),
            },
          })
          .json<{ token: string }>();

        const token = response.token;
        const livekitUrl = import.meta.env.VITE_LIVEKIT_URL;

        if (!livekitUrl) {
          throw new Error('VITE_LIVEKIT_URL environment variable is not set');
        }

        const newRoom = new Room(roomOptions);
        setupRoomListeners(newRoom);
        await newRoom.connect(livekitUrl, token);

        // Wait for room to be fully connected
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Avatar room connection timeout'));
          }, 10000); // 10 second timeout

          if (newRoom.state === ConnectionState.Connected) {
            clearTimeout(timeout);
            resolve();
          } else {
            const onStateChanged = (state: ConnectionState) => {
              if (state === ConnectionState.Connected) {
                clearTimeout(timeout);
                newRoom.off(RoomEvent.ConnectionStateChanged, onStateChanged);
                resolve();
              } else if (state === ConnectionState.Disconnected) {
                clearTimeout(timeout);
                newRoom.off(RoomEvent.ConnectionStateChanged, onStateChanged);
                reject(new Error('Avatar room disconnected during connection'));
              }
            };
            newRoom.on(RoomEvent.ConnectionStateChanged, onStateChanged);
          }
        });

        console.log('🎭 DEBUG: Successfully connected to avatar room:', config.roomName);
        setRoom(newRoom);
        setIsConnected(true);
        setIsConnecting(false);

        // Automatically start microphone for voice interaction - call with explicit state check
        console.log('🎤 DEBUG: Attempting to start microphone automatically...', {
          isMicrophoneActive,
          isConnected: true, // We just set this
          roomExists: !!newRoom,
          roomState: newRoom.state,
          connectionState: ConnectionState.Connected,
        });
        
        try {
          // Inline microphone start to avoid dependency issues
          if (isMicrophoneActive) {
            console.warn('🎤 Microphone already active, skipping auto-start');
          } else if (newRoom.state !== ConnectionState.Connected) {
            console.warn('🎤 Room not fully connected, cannot start microphone:', newRoom.state);
          } else {
            // Call startMicrophone directly with the room instance
            await startMicrophone(newRoom);
            console.log('🎤 DEBUG: Microphone started automatically for avatar session');
          }
        } catch (micError) {
          console.warn('🎤 WARNING: Failed to start microphone automatically:', micError);
          // Don't fail the entire session if microphone fails
        }

        console.log('🎭 DEBUG: Avatar session started successfully');
        callbacks.onConnect?.();
        return `${config.roomName}-${config.participantName}`;
      } catch (err) {
        console.error('🎭 ERROR: startAvatarSession error:', err);
        callbacks.onError?.(err);
        throw err;
      }
    },
    [setupRoomListeners, roomOptions, isConnected, isConnecting],
  );

  const stopAvatarSession = useCallback(async () => {
    console.log('🎭 DEBUG: stopAvatarSession called');
    disconnectFromRoom();
  }, [disconnectFromRoom]);

  // Microphone management functions with explicit permission handling
  const startMicrophone = useCallback(async (roomOverride?: Room) => {
    const targetRoom = roomOverride || room;
    const checkConnected = roomOverride ? true : isConnected;
    
    if (isMicrophoneActive || !checkConnected || !targetRoom || targetRoom.state !== ConnectionState.Connected) {
      console.warn('🎤 Cannot start microphone: room not fully connected', {
        isMicrophoneActive,
        isConnected: checkConnected,
        roomExists: !!targetRoom,
        roomState: targetRoom?.state,
        usingRoomOverride: !!roomOverride,
      });
      return;
    }

    try {
      setError(null);

      // Stop existing tracks properly
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
        audioTrackRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }

      // Check microphone permissions explicitly first
      console.log('🎤 DEBUG: Checking microphone permissions...');
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        console.log('🎤 DEBUG: Microphone permission status:', permissionStatus.state);
        
        if (permissionStatus.state === 'denied') {
          throw new Error('Microphone permission denied. Please allow microphone access and refresh the page.');
        }
      } catch (permissionError) {
        console.warn('🎤 WARNING: Could not check microphone permission:', permissionError);
        // Continue anyway - some browsers don't support permissions API
      }

      // Get user media stream with more detailed error handling
      console.log('🎤 DEBUG: Requesting getUserMedia for avatar...');
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,
          },
          video: false,
        });
      } catch (mediaError) {
        console.error('🎤 ERROR: getUserMedia failed:', mediaError);
        if (mediaError instanceof Error) {
          if (mediaError.name === 'NotAllowedError') {
            throw new Error('Microphone access denied. Please allow microphone access and try again.');
          } else if (mediaError.name === 'NotFoundError') {
            throw new Error('No microphone found. Please connect a microphone and try again.');
          } else if (mediaError.name === 'NotReadableError') {
            throw new Error('Microphone is already in use by another application.');
          }
        }
        throw new Error('Failed to access microphone: ' + (mediaError instanceof Error ? mediaError.message : 'Unknown error'));
      }

      console.log('🎤 DEBUG: getUserMedia successful for avatar:', {
        streamId: stream.id,
        audioTracks: stream.getAudioTracks().length,
        streamActive: stream.active,
      });

      // Store the stream reference
      mediaStreamRef.current = stream;

      // Get and validate audio track
      const audioTrack = stream.getAudioTracks()[0];
      if (!audioTrack) {
        throw new Error('No audio track found in media stream');
      }

      console.log('🎤 DEBUG: Audio track details for avatar:', {
        id: audioTrack.id,
        label: audioTrack.label,
        enabled: audioTrack.enabled,
        readyState: audioTrack.readyState,
        muted: audioTrack.muted,
      });

      // Wait a brief moment to ensure room is ready for publishing
      await new Promise(resolve => setTimeout(resolve, 500));

      // Publish the audio track to the room with retry logic
      console.log('🎤 DEBUG: Publishing audio track to LiveKit room...');
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          const publication = await targetRoom.localParticipant.publishTrack(
            audioTrack,
            {
              source: Track.Source.Microphone,
              name: 'user-microphone',
            },
          );
          console.log('🎤 DEBUG: Audio track published successfully for avatar:', {
            publicationSid: publication.trackSid,
            kind: publication.kind,
            source: publication.source,
            attempt: retryCount + 1,
          });
          break;
        } catch (publishError) {
          retryCount++;
          console.warn(`🎤 WARNING: Track publishing attempt ${retryCount} failed:`, publishError);
          
          if (retryCount >= maxRetries) {
            throw new Error('Failed to publish audio track after multiple attempts: ' + 
              (publishError instanceof Error ? publishError.message : 'Unknown error'));
          }
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      }

      // Enable microphone on participant
      targetRoom.localParticipant.setMicrophoneEnabled(true);
      console.log('🎤 DEBUG: Microphone enabled on participant for avatar');

      setIsMicrophoneActive(true);
      console.log('🎤 Avatar microphone started and track published successfully');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to start microphone';
      setError(errorMessage);
      console.error('❌ Error starting avatar microphone:', err);
      callbacksRef.current.onError?.(err);
      throw err;
    }
  }, [isMicrophoneActive, isConnected, room]);


  const stopMicrophone = useCallback(() => {
    console.log('🎤 DEBUG: Stopping avatar microphone...');
    
    // Stop media stream tracks
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    
    // Stop audio track
    if (audioTrackRef.current) {
      audioTrackRef.current.stop();
      audioTrackRef.current = null;
    }

    // Disable microphone on participant
    if (room?.localParticipant) {
      room.localParticipant.setMicrophoneEnabled(false);
    }

    setIsMicrophoneActive(false);
    console.log('🎤 Avatar microphone stopped');
  }, [room]);

  // Function to attach to external video container
  const attachToVideoContainer = useCallback((containerRef: React.RefObject<HTMLDivElement>) => {
    console.log('🎭 DEBUG: Attaching to video container:', !!containerRef.current);
    videoContainerRef.current = containerRef.current;
  }, []);

  // Handle connection changes
  useEffect(() => {
    if (onConnectionChange) {
      onConnectionChange(isConnected);
    }
  }, [isConnected, onConnectionChange]);

  // Handle agent speaking changes
  useEffect(() => {
    if (onAgentSpeaking) {
      onAgentSpeaking(isAgentSpeaking);
    }
  }, [isAgentSpeaking, onAgentSpeaking]);

  // Cleanup on unmount only - prevent repeated disconnections
  useEffect(() => {
    return () => {
      console.log('🎭 DEBUG: useLivekitAvatar unmounting, disconnecting room');
      if (room) {
        // Clean up event listeners
        cleanupFunctionsRef.current.forEach((cleanup) => cleanup());
        cleanupFunctionsRef.current = [];

        // Stop microphone
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((track) => track.stop());
          mediaStreamRef.current = null;
        }
        if (audioTrackRef.current) {
          audioTrackRef.current.stop();
          audioTrackRef.current = null;
        }
        setIsMicrophoneActive(false);

        // Detach video track from external container
        if (videoContainerRef.current && videoElementRef.current) {
          // Clear the container
          while (videoContainerRef.current.firstChild) {
            videoContainerRef.current.removeChild(videoContainerRef.current.firstChild);
          }
          videoElementRef.current = null;
          setIsVideoReady(false);
        }
        setAgentVideoTrack(null);
        
        // Clean up agent audio elements
        const agentAudioElements = document.querySelectorAll('audio[data-livekit-agent-audio="true"]');
        agentAudioElements.forEach((audioElement) => {
          if (audioElement.parentNode) {
            audioElement.parentNode.removeChild(audioElement);
          }
        });

        // Disconnect from room
        room.disconnect();
        setRoom(null);
        setIsConnected(false);
        setIsConnecting(false);
        setIsAgentSpeaking(false);
        setAgentState(null);
        setConnectionState(ConnectionState.Disconnected);

        console.log('🎭 DEBUG: Disconnected from LiveKit room on unmount');
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // Basic LiveKit functionality
    room,
    isConnected,
    isConnecting,
    connectionState,
    
    // Video-specific functionality
    agentVideoTrack,
    videoElementRef,
    isVideoReady,
    
    // Audio/Microphone functionality
    isMicrophoneActive,
    startMicrophone,
    stopMicrophone,
    
    // Agent state
    isAgentSpeaking,
    agentState,
    
    // Control methods
    connectAvatar,
    disconnectAvatar,
    startAvatarSession,
    stopAvatarSession,
    
    // Video container management
    attachToVideoContainer,
    
    error,
  };
};